import axios from 'axios';
import { config } from "./config";
import store from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import { checkLicenseBeforeRequest } from './licenseRequestGuard';
import { translate } from '../i18n/translate';
import { readErrorMessage, toErrorText } from './errorMessage';

const apis = axios.create({
  baseURL: `${config.baseUrl}`,
  withCredentials: true
});

type TokenProvider = () => Promise<string | null> | string | null;
export let tokenProvider: TokenProvider | null = null;
export function setTokenProvider(provider: TokenProvider) { tokenProvider = provider; }

let csrfToken: string | null = null;

export async function initializeCSRFToken() {
  try {
    const response = await apis.get('/auth/csrf-token');
    csrfToken = response.data.csrf_token;
  } catch (error) {
    // Token initialization failed silently
  }
}

let lastApiCallTime = Date.now();
const CHECK_INTERVAL = 60 * 60 * 1000;

const checkBackendStatus = async () => {
    try {
        const response = await axios.get(`${config.baseUrl}/health`, { timeout: 10000 });
        return response.status === 200;
    } catch (e: any) {
        return false;
    }
};

const shouldHealthCheck = () => Date.now() - lastApiCallTime >= CHECK_INTERVAL;

apis.interceptors.request.use(async (cfg) => {
    if (shouldHealthCheck()) {
        const ok = await checkBackendStatus();
        if (!ok) {
            store.dispatch(showFeedbackModal({
                showModal: true,
                message: translate("Server is currently unavailable. Please try again later."),
                modalType: 'error',
                duration: 5000
            }));
            return Promise.reject(new Error("Backend is unreachable"));
        }
    }

    lastApiCallTime = Date.now();

    // ─── License guard: block mutating calls to unlicensed sections/features ───
    const licenseCheck = checkLicenseBeforeRequest(cfg);
    if (!licenseCheck.allowed) {
        // Only dispatch the license-error event if the overlay is NOT already showing
        // (overlay handles the UX for locked pages; no need for duplicate modals)
        if (!(window as any).__ponsLicenseOverlayActive) {
            window.dispatchEvent(new CustomEvent('license-error', {
                detail: {
                    code: licenseCheck.code,
                    message: translate('Upgrade required'),
                    required_section: licenseCheck.section,
                    required_feature: licenseCheck.feature,
                }
            }));
        }
        return Promise.reject(new axios.Cancel('Blocked by license guard: ' + licenseCheck.code));
    }

    try {
        const token = tokenProvider ? await tokenProvider() : null;
        if (token) {
            Object.assign(cfg.headers, {
                Authorization: `Bearer ${token}`,
            });
        }

        // const method = cfg.method?.toUpperCase() || '';

        // if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        //     if (!csrfToken) {
        //         await initializeCSRFToken();
        //     }

        //     if (csrfToken) {
        //         cfg.headers['X-CSRFToken'] = csrfToken;
        //         console.log(`[CSRF] Added token to ${method} ${cfg.url}`);
        //     }
        // }
    } catch (e) {
        // Error in request interceptor - fail silently
    }

    return cfg;
});

apis.interceptors.response.use(undefined, async (error) => {
    const original = error?.config;
    if (!original || original._retried) throw error;

    // if (error?.response?.status === 400 && error?.response?.data?.code === 'CSRF_INVALID') {
    //   console.warn('[CSRF] Token invalid, fetching new one...');
    //   original._retried = true;
    //   await initializeCSRFToken();

    //   if (csrfToken) {
    //     original.headers['X-CSRFToken'] = csrfToken;
    //     return axios(original);
    //   }
    // }

    // ─── Handle licensing errors (403 with license-specific codes) ───
    if (error?.response?.status === 403) {
      const errorCode = error?.response?.data?.code;
      const licensingCodes = [
        'LICENSE_EXPIRED',
        'SECTION_NOT_LICENSED',
        'AI_FEATURE_NOT_LICENSED',
        'LICENSE_READ_ONLY',
        'USAGE_LIMIT_REACHED',
        'NO_BASE_SEAT',
      ];

      if (errorCode && licensingCodes.includes(errorCode)) {
        // If the overlay is active it already shows the lockout UX — skip modal + toast
        if (!(window as any).__ponsLicenseOverlayActive) {
          window.dispatchEvent(new CustomEvent('license-error', {
            detail: {
              code: errorCode,
              message: toErrorText(error?.response?.data?.message) || 'Upgrade required',
              error: readErrorMessage(error, ''),
              required_section: error?.response?.data?.required_section,
              required_feature: error?.response?.data?.required_feature,
              limit_key: error?.response?.data?.limit_key,
              current_count: error?.response?.data?.current_count,
              limit_value: error?.response?.data?.limit_value,
            }
          }));
        }
        // Reject as Cancel so action-level error handlers (e.g. MatterActions)
        // don't show duplicate toast notifications for licensing errors.
        return Promise.reject(new axios.Cancel('License error: ' + errorCode));
      }
    }

    // Handle session-related errors (missing, invalid, or validation failed)
    if (error?.response?.status === 401) {
      const errorCode = error?.response?.data?.code;

      if (errorCode === 'SESSION_MISSING' || errorCode === 'SESSION_INVALID' || errorCode === 'SESSION_VALIDATION_FAILED') {
        // Dispatch logout event to be handled by listeners (Sidebar will handle the complete logout flow)
        window.dispatchEvent(new CustomEvent('session-error', { detail: { code: errorCode } }));
        return Promise.reject(error);
      }

      // Handle other 401 errors (token refresh or logout)
      if (tokenProvider && !original._retried) {
        original._retried = true;
        try {
          const token = await tokenProvider();
          if (token) {
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` };
            return axios(original);
          }
        } catch (e) {
          // Token refresh failed - treat as session error
        }
      }

      // If no token provider or token refresh failed, trigger logout flow
      window.dispatchEvent(new CustomEvent('session-error', { detail: { code: 'UNAUTHORIZED' } }));
      return Promise.reject(error);
    }

    throw error;
  });

apis.interceptors.response.use(
    (res) => { lastApiCallTime = Date.now(); return res; },
    (error) => Promise.reject(error)
);

export async function fetchWithSSE(
  url: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: FormData | BodyInit;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {}
): Promise<Response> {
  const token = tokenProvider ? await tokenProvider() : null;
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    ...(options.headers || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, {
    method: options.method || 'GET',
    body: options.body,
    headers,
    credentials: 'include',
    signal: options.signal,
  });
}

export default apis;
